import React, { useMemo, useRef } from 'react';
import { Button, Checkbox, Label } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import { ChurchServiceGuestsRowDef } from 'src/components/common/rows/ChurchServiceGuestsRow';
import { chPerm, dayInTZ, scrollUp } from 'src/components/helpers';
import EventHeading from 'src/containers/events/EventHeading';
import EventService from 'src/services/api/eventService';
import reportService from 'src/services/api/reportService';
import AddAGuest from 'src/containers/experience/coordinators/addAGuest';
import BulkUploadServiceParticipantsModal from 'src/containers/events/BulkUploadServiceParticipantsModal';
import moment from 'src/services/moment_instance';
import { editServicePath } from 'src/containers/events/service-creation/serviceFormUtils';
import AGTable from 'src/components/table/aggrid';
import { getActiveFilters } from 'src/components/table/helpers';
import {
  AdminTableNetworkSection,
  AdminTableContent,
} from 'src/assets/styles/components/AdminTable';

const ChurchServiceGuests = (props) => {
  const {
    serviceAttendance,
    set_serviceAttendance,
    set_clickedEvent,
    clickedEvent,
    getEvent,
    getEvents,
    getPreviousEventNames,
    history,
  } = props;
  const [actionLoading, setActionLoading] = React.useState(false);
  const [inputs, setInputs] = React.useState({});
  const [aditionalinformationUsers, set_aditionalinformation] = React.useState([]);
  const [paidEvent, set_paidEvent] = React.useState(false);
  const [filteredRowCount, setFilteredRowCount] = React.useState(0);
  const [activeFilters, setActiveFilters] = React.useState({});
  const [reportLoading, setReportLoading] = React.useState(false);
  const gridRef = useRef(null);

  React.useEffect(() => {
    scrollUp();
  }, []);

  React.useEffect(() => {
    set_aditionalinformation(
      serviceAttendance.filter((att) => Object.keys(att.aditionalinformation || {}).length)
    );
    set_paidEvent(!!serviceAttendance.find((att) => Object.keys(att.payment || {}).length));
  }, [serviceAttendance]);

  const filterUsers = (attended, info, payment) => {
    let serviceFiltered = serviceAttendance;
    if (attended) serviceFiltered = serviceFiltered.filter((att) => att.attended);
    if (payment)
      serviceFiltered = serviceFiltered.filter((att) => Object.keys(att.payment || {}).length);
    return serviceFiltered;
  };

  const isEventDayPassed = useMemo(() => {
    if (!clickedEvent?.date) return false;
    const eventDay = moment.tz(clickedEvent.date, 'America/Bogota').startOf('day');
    const today = moment.tz('America/Bogota').startOf('day');
    return true || eventDay.isBefore(today);
  }, [clickedEvent]);

  const showSacarOptions = useMemo(() => {
    const eventName = clickedEvent?.eventSlugname || '';
    return eventName?.toLowerCase()?.includes('encuentro') && !!clickedEvent?.paidEvent;
  }, [clickedEvent]);

  const assignGuestToPending = (guestRow) => {
    const guestName = `${guestRow?.Guest?.name || ''} ${guestRow?.Guest?.lastName || ''}`.trim();
    Swal.fire({
      title: `¿Sacar a ${guestName || 'este invitado'} de ${clickedEvent.eventSlugname?.replace(
        /_/g,
        ' '
      )}?`,
      text: 'Se meterá a la lista de personas pendientes',
      showCancelButton: true,
      confirmButtonText: 'Sí, sacar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (!result.value) return;

      EventService.assignGuestToPending({ audGuest_id: guestRow.id })
        .then((response) => {
          if (response.data.success) {
            getEvent(clickedEvent.id, clickedEvent.eventSlugname);
          } else if (response.data.message) {
            Swal.fire(response.data.message);
          }
        })
        .catch((error) => {
          if (error.response?.data?.message) Swal.fire(error.response.data.message);
        });
    });
  };

  const inScreen = filterUsers(inputs.attended, inputs.info, inputs.payment);

  const openEditPage = () => {
    const { AuditoriumGuests, ...eventData } = clickedEvent;
    const date = dayInTZ(clickedEvent.date, 'YYYY-MM-DD');
    history.push({
      pathname: editServicePath(clickedEvent.id),
      search: `?date=${date}`,
      state: { service: eventData },
    });
  };

  const deleteEvent = () => {
    Swal.fire({
      title: '¿Seguro que desea eliminar este servicio?',
      showCancelButton: true,
      confirmButtonText: 'Si, Eliminar',
      cancelButtonText: 'Volver',
    }).then((result) => {
      if (!result.value) return;
      setActionLoading(true);
      EventService.deleteEvent({ id: clickedEvent.id })
        .then((response) => {
          if (response.data.success) {
            Swal.fire(response.data.message);
            set_serviceAttendance([]);
            set_clickedEvent?.({});
            getEvents?.();
            getPreviousEventNames?.();
          }
        })
        .catch((err) => {
          if (err.response?.data?.message) Swal.fire(err.response.data.message);
        })
        .finally(() => setActionLoading(false));
    });
  };

  const downloadEventReport = () => {
    if (!clickedEvent?.id || !clickedEvent?.eventSlugname) {
      return Swal.fire('No se encontró el evento seleccionado.');
    }

    setReportLoading(true);
    const dateLabel = dayInTZ(clickedEvent.date, 'YYYY-MM-DD_HH-mm');
    const fileName = `InformeAsistencia_${clickedEvent.eventSlugname}_${dateLabel}.xlsx`;

    reportService
      .getReport({
        URL: '/event/serviceReport',
        fileName,
        body: {
          eventSlugname: clickedEvent.eventSlugname,
          audEvent_id: clickedEvent.id,
        },
      })
      .catch((error) => {
        if (error.response?.data?.message) Swal.fire(error.response.data.message);
      })
      .finally(() => setReportLoading(false));
  };

  const columnNameMap = {
    identification: 'Cédula',
    fullName: 'Nombre',
    phone: 'Celular',
    attended: 'Asistió',
    aditionalinformation: 'Info Extra',
    payment: 'Info de Pago',
  };

  const getCheckboxFilters = () => {
    const checkboxFilters = {};
    if (inputs.attended) {
      checkboxFilters['Por asistencia'] = 'Sí';
    }
    if (inputs.payment) {
      checkboxFilters['Con pagos'] = 'Sí';
    }
    return checkboxFilters;
  };

  const onGridReady = (params) => {
    if (gridRef.current) {
      gridRef.current.api = params.api;
      gridRef.current.columnApi = params.columnApi;
      const displayedRowCount = params.api.getDisplayedRowCount();
      setFilteredRowCount(displayedRowCount);
      const filters = getActiveFilters(params.api, columnNameMap);
      setActiveFilters(filters);
    }
  };

  const onFilterChanged = () => {
    if (gridRef.current?.api) {
      const displayedRowCount = gridRef.current.api.getDisplayedRowCount();
      setFilteredRowCount(displayedRowCount);
      const filters = getActiveFilters(gridRef.current.api, columnNameMap);
      setActiveFilters(filters);
    }
  };

  // Update filtered count when checkbox inputs change
  React.useEffect(() => {
    if (gridRef.current?.api) {
      const displayedRowCount = gridRef.current.api.getDisplayedRowCount();
      setFilteredRowCount(displayedRowCount);
    }
  }, [inputs.attended, inputs.info, inputs.payment]);

  // Combine checkbox filters with table filters for display
  const allActiveFilters = useMemo(() => {
    const checkboxFilters = getCheckboxFilters();
    return { ...checkboxFilters, ...activeFilters };
  }, [inputs.attended, inputs.info, inputs.payment, activeFilters]);

  return (
    <AdminTableNetworkSection>
      <AdminTableContent>
        <Button
          onClick={() => {
            set_serviceAttendance([]);
            set_clickedEvent?.({});
          }}
        >
          Atrás
        </Button>
        <Button onClick={() => getEvent(clickedEvent.id, clickedEvent.eventSlugname)}>
          Refrescar
        </Button>
        <h2>Usuarios del servicio</h2>
        <EventHeading name={clickedEvent?.eventSlugname} date={clickedEvent?.date} />
        {chPerm(['Auditorio_PrincipalAdmin']) && (
          <div style={{ margin: '16px 0 0 0', display: 'flex', gap: '10px' }}>
            <Button primary disabled={actionLoading} onClick={openEditPage}>
              Modificar
            </Button>
            <Button
              color="red"
              basic
              loading={actionLoading}
              disabled={actionLoading}
              onClick={deleteEvent}
            >
              Eliminar
            </Button>
          </div>
        )}
        {chPerm(['eventAdmin']) && (
          <div style={{ margin: '20px 0 0 0' }}>
            <Button
              secondary
              loading={reportLoading}
              disabled={reportLoading}
              onClick={downloadEventReport}
            >
              Descargar reporte de este servicio
            </Button>
          </div>
        )}
        <div
          style={{
            margin: '20px 0 0 0',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          {chPerm(['eventAdmin']) && <AddAGuest getEvent={getEvent} event={clickedEvent} />}
          {chPerm(['eventAdmin']) && (
            <BulkUploadServiceParticipantsModal clickedEvent={clickedEvent} getEvent={getEvent} />
          )}
        </div>
        <div className="options" style={{ padding: '20px 0 0 0', margin: 0 }}>
          <div style={{ display: 'block', width: '100%' }}>
            <Checkbox
              label="Solo mostrar usuarios asistentes"
              name="attended"
              checked={inputs.attended}
              toggle
              onClick={() => setInputs({ ...inputs, attended: !inputs.attended })}
            />
          </div>
          {aditionalinformationUsers.length !== 0 && (
            <div style={{ display: 'block', width: '100%' }}>
              <Checkbox
                label="Mostrar la información extra de los usuarios"
                name="generaciones"
                checked={inputs.info}
                toggle
                onClick={() => setInputs({ ...inputs, info: !inputs.info })}
              />
            </div>
          )}
          {paidEvent && (
            <div style={{ display: 'block', width: '100%' }}>
              <Checkbox
                label="Solo mostrar usuarios con pagos"
                name="payment"
                checked={inputs.payment}
                toggle
                onClick={() => setInputs({ ...inputs, payment: !inputs.payment })}
              />
            </div>
          )}
        </div>
      </AdminTableContent>

      <p style={{ padding: '20px 0 0 0', margin: 0 }}>
        <strong>Total usuarios:</strong> {serviceAttendance.length}
      </p>
      <p style={{ padding: '0 0 0 0', margin: 0 }}>
        <strong>Total usuarios asistentes:</strong>{' '}
        {serviceAttendance.filter((att) => att.volunteer === null && att.attended).length}
      </p>
      {Object.keys(allActiveFilters).length > 0 && (
        <div style={{ padding: '20px 0 0 0', margin: 0 }}>
          <p style={{ margin: '0 0 10px 0' }}>
            <strong>Usuarios mostrados con filtros de la tabla:</strong> {filteredRowCount}
          </p>
          <div>
            <strong style={{ marginRight: '8px' }}>Filtros activos:</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {Object.entries(allActiveFilters).map(([column, value]) => (
                <Label key={column} color="blue" style={{ margin: '4px 0', fontWeight: '400' }}>
                  <strong>{column}:</strong> {value}
                </Label>
              ))}
            </div>
          </div>
        </div>
      )}
      <div style={{ height: '70vh', width: '100%', margin: '20px 0' }}>
        <AGTable
          columnDefs={ChurchServiceGuestsRowDef({
            aditionalinformationUsers,
            inputs,
            paidEvent,
            clickedEvent,
            isEventDayPassed,
            assignGuestToPending,
            showSacarOptions,
          })}
          rowData={inScreen}
          tableRef={gridRef}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
          pagination
          paginationPageSize={20}
          suppressDragLeaveHidesColumns={false}
          rowHeight={60}
          defaultCol={{ autoHeight: true }}
        />
      </div>
      <div style={{ padding: '20px 0 0 0', margin: 0 }}>
        {chPerm(['eventAdmin']) && (
          <div style={{ display: 'block', width: '100%' }}>
            <Checkbox
              label="Mostrar opciones avanzadas"
              name="advanced"
              checked={inputs.advanced}
              toggle
              onClick={() => setInputs({ ...inputs, advanced: !inputs.advanced })}
            />
          </div>
        )}
      </div>
    </AdminTableNetworkSection>
  );
};

export default ChurchServiceGuests;
